import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useState } from 'react';
import { formatUtilityLabel } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { AccordionHeader } from './AccordionHeader';
import { AllocationEditForm } from './AllocationEditForm';
import useStyles from './billDetailsStyles';
import { DASH, formatMoney, SESSION_ONLY_HINT } from './billDetailsUtils';
import { getAllocationSetting } from './billEditUtils';
import { UtilityRail } from './UtilityRail';
function SummaryItem(_ref) {
  let label = _ref.label,
    value = _ref.value;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.allocationSummaryItem
  }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationSummaryValue
  }, value));
}

/** Per-property allocation table: utilities on the rail, per-unit charges for the selected one. */
export function PropertyAllocation(_ref2) {
  let allocation = _ref2.allocation,
    currency = _ref2.currency,
    _ref2$defaultExpanded = _ref2.defaultExpanded,
    defaultExpanded = _ref2$defaultExpanded === void 0 ? false : _ref2$defaultExpanded,
    _ref2$isEditing = _ref2.isEditing,
    isEditing = _ref2$isEditing === void 0 ? false : _ref2$isEditing,
    onApprove = _ref2.onApprove,
    onChangeSetting = _ref2.onChangeSetting,
    settings = _ref2.settings;
  const classes = useStyles();
  const _useState = useState(defaultExpanded),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    activeIndex = _useState4[0],
    setActiveIndex = _useState4[1];
  const utilities = allocation.utilities || [];
  const safeIndex = utilities.length ? Math.min(activeIndex, utilities.length - 1) : 0;
  const utility = utilities[safeIndex];
  return /*#__PURE__*/React.createElement("div", {
    className: classes.accordion
  }, /*#__PURE__*/React.createElement(AccordionHeader, {
    isExpanded: isExpanded,
    onToggle: () => setIsExpanded(current => !current),
    title: allocation.address || DASH
  }), isExpanded ? /*#__PURE__*/React.createElement("div", {
    className: classes.accordionBody
  }, utilities.length > 0 ? /*#__PURE__*/React.createElement(UtilityRail, {
    activeIndex: safeIndex,
    onChange: setActiveIndex,
    showStatus: true,
    utilities: utilities
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.allocationPanel
  }, utilities.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, "No utilities to allocate for this property.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.utilityPanelTitleRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.utilityPanelTitle
  }, formatUtilityLabel(utility.serviceType), " allocation"), /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(utility.isApproved ? classes.statusChipDone : '')
  }, utility.isApproved ? 'Approved' : 'Pending approval')), isEditing ? /*#__PURE__*/React.createElement(AllocationEditForm, {
    currency: currency
    // The range picker holds its own dates, so switching utilities has to remount it.
    ,
    key: utility.serviceType,
    onChange: patch => onChangeSetting(allocation.propertyIndex, utility.serviceType, patch),
    setting: getAllocationSetting(settings, allocation.propertyIndex, utility.serviceType),
    utility: utility
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.allocationSummary
  }, /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Service dates",
    value: utility.servicePeriod
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationSummaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Bill amount",
    value: formatMoney(utility.billAmount, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationSummaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Deductions",
    value: "".concat(utility.deductionPercent, "%")
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationSummaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Total billing",
    value: formatMoney(utility.totalBilling, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationSummaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Recovery",
    value: "".concat(utility.recoveryPercent, "%")
  })), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.allocationRow, " ").concat(classes.allocationRowHead)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.allocationUnit
  }, "Unit #"), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationResident
  }, "Resident"), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationOccupants
  }, "Occ."), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationFactor
  }, "Occ. factor"), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationDays
  }, "Days"), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationTotal
  }, "Utility total")), (utility.units || []).map((unit, index) => /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.allocationRow, " ").concat(index === utility.units.length - 1 ? classes.allocationRowLast : ''),
    key: unit.unit
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.allocationUnit
  }, unit.unit), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationResident,
    title: unit.resident
  }, unit.resident), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationOccupants
  }, unit.occupants), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationFactor
  }, unit.occupancyFactor.toFixed(1)), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationDays
  }, unit.days), /*#__PURE__*/React.createElement("span", {
    className: classes.allocationTotal
  }, formatMoney(unit.total, currency)))), isEditing ? null : /*#__PURE__*/React.createElement("div", {
    className: classes.panelActions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: SESSION_ONLY_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onApprove || utility.isApproved,
    onClick: () => onApprove(utility.key),
    size: "small",
    variant: "text"
  }, utility.isApproved ? 'Allocated' : 'Approve & allocate'))))))) : null);
}